import { Fragment, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { BrandMark, Icon } from './primitives';
import { useAccountFeature } from '../account-features';
import { type DesktopRelease, GET_PATH, INSTALL_PATH, useDesktopApp } from '../desktop-app';
import { GUIDES_PATH } from '../guides';
import { linkProps } from '../router';
import { createHref } from '../surface';

/**
 * The frame everything outside the dashboard chrome renders into: the guide pages, the
 * tester's redeem flow, and the nine small flows.
 *
 * The header offers "Redeem a code" to everybody, signed in or not — the audience most likely
 * to be on the wrong page is a tester who followed a link here, and the cheapest fix is to
 * point at the right one from every page rather than only from the one they landed on.
 * Creators is there for the same reason and a stronger one: the front door is the player's
 * page, so the wordmark already leads there, and a creator who arrived on `/install` from a
 * search has no other way to find the page that argues their case.
 */

/**
 * The footer's links, for a site with a released desktop app or without one.
 *
 * The way to the app is a link only when there is an app (`desktop-app.ts`): with none, the footer
 * of every public page names the one way a game is installed today and nothing else. With one,
 * `/install` is there for an account that has the install by hand, under the name that tells it
 * apart from the app — and for nobody else.
 */
export function footerLinks(app: DesktopRelease | null, byHand = false): [string, string][] {
  return [
    ['BoardFlight', '/'],
    ['Creators', '/creators'],
    ...(app ? ([['Get BoardFlight', GET_PATH]] satisfies [string, string][]) : []),
    // With an app, the install by hand is the second way and only an account that was given it
    // is told of it (`docs/library-first-plan.md` §6). With none, it is the only page there is.
    ...(app && !byHand ? [] : ([[app ? 'Install without the app' : 'Install a game', INSTALL_PATH]] satisfies [string, string][])),
    ['Guides', GUIDES_PATH],
    ['Publishing', '/start'],
    ['Unity plugin', '/downloads'],
    ['AI agents', '/mcp'],
    ['Terms', '/terms'],
    ['Privacy', '/privacy'],
  ];
}

/** The device this whole product exists to serve, and — per the footer — somebody else's. */
export const BOARD_SITE = 'https://board.fun';
/** Where a reader who has not started yet gets the Unity, Web or Godot SDK and the simulator.
 *  Pointing at it rather than at the homepage is the honest answer to "do I need a Board to
 *  begin?" — you do not, and saying so costs us nothing and them a purchase. */
export const BOARD_CREATE = 'https://board.fun/pages/create';

/** Whoever is answerable for this. Named in the footer next to the disclaimer, because a
 *  disclaimer that says who it is *not* and never says who it *is* reads as evasive. */
export const MAKER = { name: 'Boardaux Studios', url: 'https://boardaux.com' };

export function PublicFrame({
  signedIn = false,
  email,
  /** Let the page own its own gutters and go edge to edge. Only the landing page does: its
   *  bands are full-width colour, and the frame's centred 1040px column would cut them into
   *  floating rectangles. Every other public page is prose and wants the column. */
  bleed = false,
  backdrop,
  children,
}: {
  signedIn?: boolean;
  email?: string;
  bleed?: boolean;
  /** A picture for the whole page to stand on, as a game's library page stands on its hero.
   *  The page turns dark around it — the frame's colours are tokens, so the header, the
   *  footer and everything inside follow without a second stylesheet. Only a share page passes
   *  one. */
  backdrop?: string | null;
  children: ReactNode;
}) {
  return (
    <div
      className={backdrop ? 'bf-public has-backdrop' : 'bf-public'}
      style={backdrop ? ({ '--bf-backdrop': `url(${JSON.stringify(backdrop)})` } as CSSProperties) : undefined}
    >
      {backdrop ? <div className="bf-public-backdrop" aria-hidden="true" /> : null}
      <header className="bf-public-head">
        {/* Home, not the guide: the wordmark is the one link every reader assumes goes to the
            front door, and the front door is now a page of its own. */}
        <a className="bf-public-wordmark" {...linkProps('/')}>
          <BrandMark />
          BoardFlight
        </a>
        <nav className="bf-public-nav">
          <a {...linkProps('/library')}>Library</a>
          <a {...linkProps('/creators')}>Creators</a>
          {/* "a code" is dropped at phone width, where four links have to share one row
              (`.bf-public-nav-more` in kit.css). The link keeps its whole name either way. */}
          <a aria-label="Redeem a code" {...linkProps('/redeem')}>
            Redeem<span className="bf-public-nav-more"> a code</span>
          </a>
          <a className="is-strong" {...linkProps(signedIn ? createHref() : '/signin')}>
            {signedIn ? 'Dashboard →' : 'Sign in →'}
          </a>
        </nav>
      </header>

      <main className={bleed ? 'bf-public-main is-bleed' : 'bf-public-main'}>{children}</main>

      <PublicFoot signedIn={signedIn} email={email} />
    </div>
  );
}

/**
 * What every public page ends on: the site's links, and the sentence that says whose project this
 * is not. Its own component because the library draws its own frame (`storefront/frame.tsx`) and
 * must end on the same sentence — one copy of a disclaimer, not two that drift.
 */
export function PublicFoot({ signedIn = false, email }: { signedIn?: boolean; email?: string }) {
  const { release } = useDesktopApp();
  const byHand = useAccountFeature('install.byhand');
  return (
    <footer className="bf-public-foot">
      <div className="bf-public-foot-links">
        {footerLinks(release, byHand).map(([label, href]) => (
          <a key={label} {...linkProps(href)}>{label}</a>
        ))}
        <a href={BOARD_SITE} target="_blank" rel="noreferrer noopener">Board ↗</a>
        <span className="bf-grow" />
        {signedIn && email ? <span>{email}</span> : null}
      </div>

      <Disclaimer />
    </footer>
  );
}

/**
 * Whose project this is, and whose it is not.
 *
 * Not small print for its own sake: BoardFlight is named after somebody else's device and reads
 * like a first-party service, so the one place every page ends has to say plainly that it is not
 * one. In the frame rather than on the landing page so it is true of `/redeem` too — the page a
 * tester who has never heard of us lands on — and its own component for the one page that has no
 * frame around it, the onboarding's (`storefront/frame.tsx`).
 */
export function Disclaimer() {
  return (
    <p className="bf-public-foot-fine">
      BoardFlight is an independent, unofficial project. It is not affiliated with, endorsed
      by, sponsored by or connected to{' '}
      <a href={BOARD_SITE} target="_blank" rel="noreferrer noopener">board.fun</a> or the
      makers of Board; “Board” names the device these builds run on and nothing more. Made by{' '}
      <a href={MAKER.url} target="_blank" rel="noreferrer noopener">{MAKER.name}</a>.
    </p>
  );
}

/**
 * A numbered step in a guide.
 *
 * A step already done reads as done rather than as numbered, so it stops competing with what
 * is still to do. The redeem flow ticks them off as they complete and renumbers what remains —
 * the install sequence continues from where the sign-up left off rather than restarting at 1,
 * because a list that starts again at 1 halfway down reads as a second, separate job.
 */
export function Step({
  n,
  title,
  done = false,
  last = false,
  children,
}: {
  n: number | string;
  title: ReactNode;
  done?: boolean;
  last?: boolean;
  children?: ReactNode;
}) {
  return (
    <section className={last ? 'bf-step is-last' : 'bf-step'}>
      <div className="bf-step-rail">
        <span className={done ? 'bf-step-num is-done' : 'bf-step-num'}>
          {done ? <Icon name="check" size={26} /> : n}
        </span>
        {!last ? <span className="bf-step-line" /> : null}
      </div>
      <div className="bf-step-body">
        <h3 className={done ? 'bf-step-title is-done' : 'bf-step-title'}>{title}</h3>
        <div className="bf-step-content">{children}</div>
      </div>
    </section>
  );
}

/**
 * The part of a page that one reader in twenty came for.
 *
 * Digests, pinned URLs, CI tokens, keystore warnings: all true, all occasionally load-bearing,
 * and all of it read by someone who already knows they want it. Left inline it pushes the two
 * steps everybody needs below the fold and makes an ordinary install read like a security
 * procedure.
 *
 * `<details>`, not a JS toggle — everything stays in the DOM, so find-in-page and anyone
 * arriving from a search still land on it, and it opens before React has run. The summary says
 * what is inside rather than only "Advanced", so nobody has to open it to find out.
 */
export function Advanced({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details className="bf-advanced">
      <summary>{summary}</summary>
      <div className="bf-advanced-body">{children}</div>
    </details>
  );
}

/** Somewhere to go in another UI, not something to type: Window ▸ BoardFlight ▸ Publish. */
export function Path({ children }: { children: string }) {
  const parts = children.split(/\s*(?:▸|>)\s*/);
  return (
    <span className="bf-path">
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index ? <span className="bf-path-sep" aria-hidden="true">▸</span> : null}
          <span>{part}</span>
        </Fragment>
      ))}
    </span>
  );
}

/**
 * A copyable shell block that reports its own copy outcome.
 *
 * Clipboard access can be refused — insecure context, permissions, a browser that just says no
 * — so the control says what happened rather than doing nothing that looks like success, and
 * the text stays selectable either way. `role="status"` because "did that work?" is otherwise
 * unanswerable without sight.
 */
export function Command({ label, lines }: { label?: ReactNode; lines: string | string[] }) {
  const rows = Array.isArray(lines) ? lines : [lines];
  const [said, setSaid] = useState<string | null>(null);

  async function copy() {
    try {
      await navigator.clipboard.writeText(rows.join('\n'));
      setSaid('Copied');
    } catch {
      setSaid('Press ⌘C');
    }
    setTimeout(() => setSaid(null), 2400);
  }

  return (
    <div className="bf-command">
      {label ? <div className="bf-command-label">{label}</div> : null}
      <div className="bf-command-box">
        <pre className="bf-command-pre">{rows.join('\n')}</pre>
        {said ? <span className="bf-command-said" role="status">{said}</span> : null}
        <button type="button" className="bf-command-copy" aria-label="Copy" onClick={copy}>
          <Icon name="content_copy" size={18} />
        </button>
      </div>
    </div>
  );
}

/**
 * Two or three routes to the same outcome, inside one step.
 *
 * Not a screen's SectionSwitcher: these are alternatives, not sections, and the order is
 * deliberate — descending by how much the reader has to already know.
 */
export function InlineSwitcher<T extends string>({
  options,
  activeId,
  onSelect,
  label,
}: {
  options: { id: T; label: string }[];
  activeId: T;
  onSelect: (id: T) => void;
  label: string;
}) {
  return (
    <div className="bf-inline-switcher" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={option.id === activeId}
          className={option.id === activeId ? 'bf-inline-option is-on' : 'bf-inline-option'}
          onClick={() => onSelect(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A code read off another screen — an editor panel, a terminal, a chat — and typed here.
 *
 * Character legibility beats everything: mono, oversized, wide tracking. Case, spaces and
 * dashes are normalised server-side, so nothing is rejected here for its formatting — a
 * tester who typed lower case has not made a mistake worth a red border.
 */
export function CodeEntry({
  value = '',
  placeholder = 'WXYZ-4821',
  invalid = false,
  onChange,
  ...rest
}: {
  value?: string;
  placeholder?: string;
  invalid?: boolean;
  onChange?: (value: string) => void;
  id?: string;
  'aria-label'?: string;
}) {
  return (
    <input
      className={invalid ? 'bf-codeentry is-invalid' : 'bf-codeentry'}
      value={value}
      placeholder={placeholder}
      spellCheck={false}
      autoCapitalize="characters"
      autoComplete="off"
      aria-invalid={invalid || undefined}
      onChange={(event) => onChange?.(event.target.value.toUpperCase())}
      {...rest}
    />
  );
}
